import { useEffect, useRef, useCallback } from 'react';

export function useMarkerSync(audioElement, sections, onSectionChange) {
  const lastIndexRef = useRef(-1);
  const rafRef = useRef(null);
  const onSectionChangeRef = useRef(onSectionChange);
  const sectionsRef = useRef(sections);

  useEffect(() => { onSectionChangeRef.current = onSectionChange; }, [onSectionChange]);
  useEffect(() => { sectionsRef.current = sections; }, [sections]);

  const startLoop = useCallback((audio) => {
    const tick = () => {
      const currentSections = sectionsRef.current;
      if (!audio.paused && currentSections && currentSections.length > 0) {
        const t = audio.currentTime;

        let idx = -1;
        let lo = 0, hi = currentSections.length - 1;
        while (lo <= hi) {
          const mid = (lo + hi) >> 1;
          if (currentSections[mid].startTime <= t) {
            idx = mid;
            lo = mid + 1;
          } else {
            hi = mid - 1;
          }
        }

        if (idx !== lastIndexRef.current && idx >= 0) {
          lastIndexRef.current = idx;
          onSectionChangeRef.current(currentSections[idx]);
        }

        rafRef.current = requestAnimationFrame(tick);
      }
    };

    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  const stopLoop = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!audioElement || !sections || sections.length === 0) return;

    const handlePlay = () => startLoop(audioElement);
    const handlePause = () => stopLoop();
    const handleEnded = () => stopLoop();
    const handleSeeked = () => { lastIndexRef.current = -1; };

    audioElement.addEventListener('play', handlePlay);
    audioElement.addEventListener('pause', handlePause);
    audioElement.addEventListener('ended', handleEnded);
    audioElement.addEventListener('seeked', handleSeeked);

    if (!audioElement.paused) {
      startLoop(audioElement);
    }

    return () => {
      stopLoop();
      audioElement.removeEventListener('play', handlePlay);
      audioElement.removeEventListener('pause', handlePause);
      audioElement.removeEventListener('ended', handleEnded);
      audioElement.removeEventListener('seeked', handleSeeked);
      lastIndexRef.current = -1;
    };
  }, [audioElement, sections, startLoop, stopLoop]);
}
